// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import BeerCard from '../../components/beer/BeerCard';
import AddBeerDialog from '../../components/beer/AddBeerDialog';
import StickyButtonBar from '../../components/common/StickyButtonBar';
import useFeaturedBeersDocuments from '../../hooks/useFeaturedBeersDocuments';
import useBreweryLocationLabelsForBeers from '../../hooks/useBreweryLocationLabelsForBeers';

const centerSpinnerBoxSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 240,
};

function EventFeaturedBeersPanel({
  eventId,
  featuredBeerIds,
  removeFeaturedBeersFromEvent,
  onAfterBeerRemoved,
}) {
  const [featuredReloadKey, setFeaturedReloadKey] = useState(0);
  const { beers, loading, error, missingIds } = useFeaturedBeersDocuments(
    featuredBeerIds,
    featuredReloadKey,
  );
  const {
    breweryNameById,
    groupedBreweryIds,
    error: locationLabelsError,
  } = useBreweryLocationLabelsForBeers(beers);

  const [selectedBeerIds, setSelectedBeerIds] = useState([]);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [staleAlertDismissed, setStaleAlertDismissed] = useState(false);

  const [editBeerDialogOpen, setEditBeerDialogOpen] = useState(false);
  const [editingBeer, setEditingBeer] = useState(null);

  const missingIdsKey = missingIds.join('\u0001');

  useEffect(() => {
    setStaleAlertDismissed(false);
  }, [missingIdsKey]);

  useEffect(() => {
    setSelectedBeerIds((previous) =>
      previous.filter((beerId) =>
        beers.some((beerRow) => beerRow.id === beerId),
      ),
    );
  }, [beers]);

  const handleToggleBeerSelect = useCallback((beerId, isSelected) => {
    if (!beerId) return;
    setSelectedBeerIds((previous) => {
      if (isSelected) {
        if (previous.includes(beerId)) return previous;
        return [...previous, beerId];
      }
      return previous.filter((id) => id !== beerId);
    });
  }, []);

  const handleOpenRemoveConfirm = useCallback(() => {
    if (!selectedBeerIds.length) return;
    setRemoveConfirmOpen(true);
  }, [selectedBeerIds.length]);

  const handleCloseRemoveConfirm = useCallback(() => {
    if (!removeSubmitting) {
      setRemoveConfirmOpen(false);
    }
  }, [removeSubmitting]);

  const handleConfirmRemove = useCallback(async () => {
    if (!eventId || !selectedBeerIds.length) return;
    setRemoveSubmitting(true);
    try {
      await removeFeaturedBeersFromEvent(eventId, selectedBeerIds);
      setSelectedBeerIds([]);
      setRemoveConfirmOpen(false);
      if (onAfterBeerRemoved) {
        await onAfterBeerRemoved();
      }
    } catch {
      // Parent / hook surface errors; keep dialog open for retry
    } finally {
      setRemoveSubmitting(false);
    }
  }, [
    eventId,
    selectedBeerIds,
    removeFeaturedBeersFromEvent,
    onAfterBeerRemoved,
  ]);

  const handleEditBeer = useCallback((beer) => {
    setEditingBeer(beer);
    setEditBeerDialogOpen(true);
  }, []);

  const handleCloseEditBeerDialog = useCallback(() => {
    setEditBeerDialogOpen(false);
    setEditingBeer(null);
  }, []);

  const handleEditBeerSuccess = useCallback(() => {
    setFeaturedReloadKey((previous) => previous + 1);
    handleCloseEditBeerDialog();
  }, [handleCloseEditBeerDialog]);

  const handleDismissStaleAlert = useCallback(() => {
    setStaleAlertDismissed(true);
  }, []);

  const removeDisabled =
    !selectedBeerIds.length || loading || Boolean(error);
  const showStaleAlert =
    missingIds.length > 0 && !staleAlertDismissed && !loading;

  let panelBody = null;

  if (loading) {
    panelBody = (
      <Box sx={centerSpinnerBoxSx}>
        <CircularProgress />
      </Box>
    );
  } else if (error) {
    panelBody = (
      <Typography variant="body2" color="error" sx={{ py: 2 }}>
        {error}
      </Typography>
    );
  } else if (!beers.length && !missingIds.length) {
    panelBody = (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No featured beers yet. Add beers from the Beer page or a brewery&apos;s beer tab.
      </Typography>
    );
  } else if (!beers.length) {
    panelBody = (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Featured beer details could not be loaded for the IDs stored on this event.
      </Typography>
    );
  } else {
    panelBody = (
      <>
        {locationLabelsError ? (
          <Typography variant="body2" color="error" sx={{ py: 1 }}>
            {locationLabelsError}
          </Typography>
        ) : null}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          {beers.map((beer) => (
            <Grid item xs={12} sm={6} md={4} key={beer.id}>
              <BeerCard
                beer={beer}
                selected={selectedBeerIds.includes(beer.id)}
                onToggleSelect={handleToggleBeerSelect}
                onEdit={beer.breweryId ? handleEditBeer : null}
                breweryNameById={breweryNameById}
                groupedBreweryIds={groupedBreweryIds}
              />
            </Grid>
          ))}
        </Grid>
      </>
    );
  }

  return (
    <Box>
      <StickyButtonBar>
        <Button
          variant="contained"
          color="error"
          aria-label="Remove selected beers from this event"
          disabled={removeDisabled}
          onClick={handleOpenRemoveConfirm}
          sx={{ mr: 1 }}
        >
          Remove selected
        </Button>
      </StickyButtonBar>

      {showStaleAlert ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={handleDismissStaleAlert}
        >
          {missingIds.length === 1
            ? 'One featured beer could not be loaded from search (it may have been removed from the index).'
            : `${missingIds.length} featured beers could not be loaded from search (they may have been removed from the index).`}
          {missingIds.length <= 5 ? (
            <Typography variant="caption" component="div" sx={{ mt: 1, display: 'block' }}>
              IDs: {missingIds.join(', ')}
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      {panelBody}

      <Dialog
        open={removeConfirmOpen}
        onClose={handleCloseRemoveConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove featured beers?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove {selectedBeerIds.length}{' '}
            {selectedBeerIds.length === 1 ? 'beer' : 'beers'} from this event&apos;s featured list?
            This does not delete the beer record.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveConfirm} disabled={removeSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmRemove}
            disabled={removeSubmitting}
          >
            {removeSubmitting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddBeerDialog
        open={editBeerDialogOpen}
        mode="edit"
        editBeer={editingBeer}
        lockedBreweryId={editingBeer?.breweryId ?? ''}
        lockedBreweryName={editingBeer?.breweryName ?? ''}
        onClose={handleCloseEditBeerDialog}
        onSuccess={handleEditBeerSuccess}
      />
    </Box>
  );
}

EventFeaturedBeersPanel.propTypes = {
  eventId: PropTypes.string.isRequired,
  featuredBeerIds: PropTypes.arrayOf(PropTypes.string),
  removeFeaturedBeersFromEvent: PropTypes.func.isRequired,
  onAfterBeerRemoved: PropTypes.func,
};

EventFeaturedBeersPanel.defaultProps = {
  featuredBeerIds: [],
  onAfterBeerRemoved: null,
};

export default EventFeaturedBeersPanel;
